import React, { useState, useEffect, useCallback, memo } from "react";
import {
  FaSnowflake,
  FaUmbrellaBeach,
  FaTree,
  FaSun,
  FaSearch,
} from "react-icons/fa";
import { useTheme } from "../ThemeContext";
import styled from "styled-components";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../Config/config";

// Styled Components
const BannerContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100vh;
  background-image: url(${(props) => props.bgImage});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  overflow: hidden;

  @media (max-width: 768px) {
    height: 100vh;
    min-height: 100vh;
  }
`;

const BannerContent = styled.div`
  width: 80%;
  max-width: 800px;
  position: relative;
  z-index: 1;

  @media (max-width: 768px) {
    width: 90%;
  }
`;

const SearchBar = styled.form`
  display: flex;
  background: ${(props) =>
    props.isDarkMode ? "rgba(0, 0, 0, 0.7)" : "rgba(255, 255, 255, 0.7)"};
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  border-radius: 10px;
  overflow: hidden;
  height: 60px;

  @media (max-width: 768px) {
    flex-direction: column;
    height: auto;
  }
`;

const SearchInput = styled.input`
  flex: 1;
  padding: 15px 20px;
  font-size: 18px;
  border: none;
  outline: none;
  background-color: transparent;
  color: ${(props) => (props.isDarkMode ? "white" : "black")};
  &::placeholder {
    color: ${(props) =>
      props.isDarkMode ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.7)"};
  }

  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const SearchButton = styled.button`
  padding: 15px 30px;
  border: none;
  background-color: ${(props) => props.theme.buttonColor};
  color: white;
  cursor: pointer;
  font-size: 18px;
  font-weight: bold;
  display: flex;
  align-items: center;
  gap: 10px;
  transition: background-color 0.3s ease;
  &:hover {
    background-color: ${(props) => props.theme.accentColor};
  }

  @media (max-width: 768px) {
    width: 100%;
    font-size: 16px;
  }
`;

const AutocompleteDropdown = styled.div`
  background-color: white;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  margin-top: 10px;
  max-height: 200px;
  overflow-y: auto;
  border-radius: 5px;
`;

const AutocompleteItem = styled.div`
  padding: 10px 15px;
  cursor: pointer;
  &:hover {
    background-color: #f0f0f0;
  }
`;

const NoHotelsMessage = styled.div`
  color: white;
  background-color: rgba(0, 0, 0, 0.7);
  padding: 10px;
  margin-top: 10px;
  text-align: center;
  border-radius: 5px;
`;

const ThemeButtons = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 20px;
  margin-top: 20px;

  @media (max-width: 768px) {
    gap: 10px;
  }
`;

const ThemeButton = styled.button`
  background: ${(props) =>
    props.isDarkMode ? "rgba(0, 0, 0, 0.7)" : "rgba(255, 255, 255, 0.7)"};
  border: none;
  padding: 15px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  border-radius: 10px;
  width: 80px;
  height: 80px;
  color: ${(props) => (props.isDarkMode ? "white" : "black")};
  transition: all 0.3s ease;
  ${(props) =>
    props.isActive &&
    `
    background-color: ${props.theme.buttonColor};
    color: ${props.theme.textColor};
    transform: scale(1.1);
  `}
  &:hover {
    background-color: ${(props) => props.theme.accentColor};
    color: ${(props) => props.theme.textColor};
  }

  @media (max-width: 768px) {
    width: 70px;
    height: 70px;
    padding: 10px;
  }
`;

const ButtonIcon = styled.span`
  font-size: 24px;
  margin-bottom: 5px;
  color: white;

  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

const ButtonText = styled.span`
  font-size: 12px;
  color: white;

  @media (max-width: 768px) {
    font-size: 10px;
  }
`;

const WavyBottom = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  overflow: hidden;
  line-height: 0;
  transform: rotate(180deg);
  svg {
    position: relative;
    display: block;
    width: calc(100% + 1.3px);
  }
  .shape-fill {
    fill: ${(props) => props.theme.buttonColor};
    transition: fill 0.3s ease;
  }
`;

const EffectContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  overflow: hidden;
  z-index: 0;
`;

const Particle = styled.div`
  position: absolute;
  top: -10px;
  width: ${(props) => props.size || "5px"};
  height: ${(props) => props.size || "5px"};
  background: ${(props) => props.color || "rgba(255, 255, 255, 0.3)"};
  border-radius: 50%;
  animation: ${(props) => props.animationName} linear infinite;
  animation-duration: ${(props) => props.duration || "3s"};
  animation-delay: ${(props) => props.delay || "0s"};
`;

const createParticles = (count, ParticleComponent) =>
  Array.from({ length: count }).map((_, index) => (
    <ParticleComponent
      key={index}
      style={{
        left: `${Math.random() * 100}%`,
        animationDuration: `${Math.random() * 3 + 2}s`,
        animationDelay: `${Math.random() * 5}s`,
      }}
    />
  ));

const SnowfallEffect = memo(() => (
  <EffectContainer>
    {createParticles(
      50,
      styled(Particle)`
        @keyframes snowfall {
          0% { transform: translateY(-10px) rotate(0deg); }
          100% { transform: translateY(calc(100vh + 10px)) rotate(360deg); }
        }
        animation-name: snowfall;
      `
    )}
  </EffectContainer>
));

const RainEffect = memo(({ type }) => {
  const isBeach = type === "beach";
  const particleCount = isBeach ? 100 : 50;
  const minDuration = isBeach ? 0.3 : 1.5;
  const maxDuration = isBeach ? 0.6 : 2.5;

  return (
    <EffectContainer>
      {createParticles(
        particleCount,
        styled(Particle)`
          @keyframes rainfall {
            0% { transform: translateY(-20px) ${isBeach ? "rotate(15deg)" : ""}; }
            100% { transform: translateY(calc(100vh + 20px)) ${isBeach ? "rotate(15deg)" : ""}; }
          }
          animation-name: rainfall;
          width: ${isBeach ? "2px" : "1px"};
          height: ${isBeach ? "30px" : "20px"};
          background: linear-gradient(transparent, #8bd3ff);
          animation-duration: ${(props) =>
            `${(
              Math.random() * (maxDuration - minDuration) + minDuration
            ).toFixed(2)}s`};
        `
      )}
    </EffectContainer>
  );
});

const DesertWindEffect = memo(() => (
  <EffectContainer>
    {createParticles(
      50,
      styled(Particle)`
        @keyframes windyDust {
          0% { transform: translateY(-5px) translateX(0); }
          100% { transform: translateY(calc(100vh + 5px)) translateX(200px); }
        }
        animation-name: windyDust;
        background: #d2b48c;
        width: 3px;
        height: 3px;
      `
    )}
  </EffectContainer>
));

// Main Banner Component
const Banner = ({ onLocationSelect, onLocationTypeSelect }) => {
  const { isDarkMode, currentTheme, changeTheme, themes } = useTheme();
  const [searchQuery, setSearchQuery] = useState("");
  const [locations, setLocations] = useState([]);
  const [filteredLocations, setFilteredLocations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [noHotelsMessage, setNoHotelsMessage] = useState("");

  // Fetch Locations from Hotels and Activities
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);

        // Fetch hotels
        const hotelsCollection = collection(db, "hotels");
        const hotelsSnapshot = await getDocs(hotelsCollection);
        const hotelsList = hotelsSnapshot.docs.map((doc) => ({
          id: doc.id,
          location: doc.data().hotelLocation,
          type: doc.data().hotelLocationType,
        }));

        // Fetch activities
        const activitiesCollection = collection(db, "activities");
        const activitiesSnapshot = await getDocs(activitiesCollection);
        const activitiesList = activitiesSnapshot.docs.map((doc) => ({
          id: doc.id,
          location: doc.data().activityLocation,
          type: doc.data().activityLocationType,
        }));

        // Combine and deduplicate locations
        const combinedLocations = [...hotelsList, ...activitiesList];
        const uniqueLocationsMap = new Map();
        combinedLocations.forEach((loc) => {
          if (loc.location && loc.type) {
            uniqueLocationsMap.set(loc.location.toLowerCase(), {
              location: loc.location,
              type: loc.type,
            });
          }
        });
        const uniqueLocations = Array.from(uniqueLocationsMap.values());

        setLocations(uniqueLocations);
        setIsLoading(false);
      } catch (err) {
        console.error("Failed to fetch data:", err);
        setError("Failed to load locations. Please try again.");
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  // Search Handlers
  const handleSearchChange = useCallback(
    (e) => {
      const query = e.target.value.trim().toLowerCase();
      setSearchQuery(query);
      setNoHotelsMessage("");
      if (query) {
        const filtered = locations.filter((loc) =>
          loc.location.toLowerCase().includes(query)
        );
        setFilteredLocations(filtered);
      } else {
        setFilteredLocations([]);
      }
    },
    [locations]
  );

  const handleLocationSelect = useCallback(
    (location) => {
      setSearchQuery(location.location);
      setFilteredLocations([]);
      onLocationSelect(location.location, location.type);
      changeTheme(location.type);
    },
    [onLocationSelect, changeTheme]
  );

  const handleSearchSubmit = useCallback(
    (e) => {
      e.preventDefault();
      const query = searchQuery.trim().toLowerCase();
      const matched = locations.find((loc) =>
        loc.location.toLowerCase().includes(query)
      );
      if (matched) {
        onLocationSelect(matched.location, matched.type);
        changeTheme(matched.type);
        setNoHotelsMessage("");
      } else {
        setNoHotelsMessage("No hotels or activities available at this location.");
        onLocationSelect(null, null);
      }
    },
    [searchQuery, locations, onLocationSelect, changeTheme]
  );

  const handleThemeChange = useCallback(
    (theme) => {
      changeTheme(theme);
      onLocationTypeSelect(theme);
      setSearchQuery("");
      onLocationSelect(null, theme);
    },
    [changeTheme, onLocationSelect, onLocationTypeSelect]
  );

  // Theme Icon Mapping
  const getThemeIcon = useCallback((type) => {
    const icons = {
      snow: <FaSnowflake />,
      beach: <FaUmbrellaBeach />,
      mountain: <FaTree />,
      desert: <FaSun />,
    };
    return icons[type] || <FaUmbrellaBeach />;
  }, []);

  // Weather Effect Mapping
  const getWeatherEffect = useCallback(() => {
    const effects = {
      snow: <SnowfallEffect />,
      forest: <RainEffect type="forest" />,
      desert: <DesertWindEffect />,
    };
    return effects[currentTheme] || null;
  }, [currentTheme]);

  // Render Logic
  if (isLoading) return <div>Loading...</div>;
  if (error) return <div>{error}</div>;

  return (
    <BannerContainer bgImage={themes[currentTheme].bgImage}>
      {getWeatherEffect()}
      <BannerContent>
        <SearchBar isDarkMode={isDarkMode} onSubmit={handleSearchSubmit}>
          <SearchInput
            type="text"
            value={searchQuery}
            onChange={handleSearchChange}
            placeholder="Search your destination"
            isDarkMode={isDarkMode}
          />
          <SearchButton theme={themes[currentTheme]}>
            <FaSearch /> Search
          </SearchButton>
        </SearchBar>
        {filteredLocations.length > 0 && (
          <AutocompleteDropdown>
            {filteredLocations.map((loc) => (
              <AutocompleteItem
                key={`${loc.location}-${loc.type}`}
                onClick={() => handleLocationSelect(loc)}
              >
                {loc.location}
              </AutocompleteItem>
            ))}
          </AutocompleteDropdown>
        )}
        {noHotelsMessage && <NoHotelsMessage>{noHotelsMessage}</NoHotelsMessage>}
        <ThemeButtons>
          {Object.entries(themes).map(([key, theme]) => (
            <ThemeButton
              key={key}
              onClick={() => handleThemeChange(key)}
              isActive={currentTheme === key}
              isDarkMode={isDarkMode}
              theme={theme}
              aria-label={`Switch to ${theme.name} theme`}
            >
              <ButtonIcon>{getThemeIcon(key)}</ButtonIcon>
              <ButtonText>{theme.name}</ButtonText>
            </ThemeButton>
          ))}
        </ThemeButtons>
      </BannerContent>
      <WavyBottom theme={themes[currentTheme]}>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 1100 160"
          preserveAspectRatio="none"
          className="w-full h-[150px] md:h-[160px]"
        >
          <path
            d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z"
            opacity=".25"
            className="shape-fill"
          />
          <path
            d="M0,0V15.81C13,36.92,27.64,56.86,47.69,72.05,99.41,111.27,165,111,224.58,91.58c31.15-10.15,60.09-26.07,89.67-39.8,40.92-19,84.73-46,130.83-49.67,36.26-2.85,70.9,9.42,98.6,31.56,31.77,25.39,62.32,62,103.63,73,40.44,10.79,81.35-6.69,119.13-24.28s75.16-39,116.92-43.05c59.73-5.85,113.28,22.88,168.9,38.84,30.2,8.66,59,6.17,87.09-7.5,22.43-10.89,48-26.93,60.65-49.24V0Z"
            opacity=".5"
            className="shape-fill"
          />
          <path
            d="M0,0V5.63C149.93,59,314.09,71.32,475.83,42.57c43-7.64,84.23-20.12,127.61-26.46,59-8.63,112.48,12.24,165.56,35.4C827.93,77.22,886,95.24,951.2,90c86.53-7,172.46-45.71,248.8-84.81V0Z"
            className="shape-fill"
          />
        </svg>
      </WavyBottom>
    </BannerContainer>
  );
};

export default memo(Banner);